import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css } from 'glamor'
import { throttle } from 'lodash-es'
import { Portal } from 'react-portal'

import { Theme } from '../../theme'

import { Touchable } from '../touchable'
import { FadeIn } from '../fade-in'

import { removeScrollListener, addScrollListener } from './utils'
import getStyles from './styles'

const arrowSize = 6

const stopPropagation = (e: React.SyntheticEvent) => {
  if (!e || !e.stopPropagation) return
  e.stopPropagation()
  e.preventDefault()
}
export type PopupVariant = 'default' | 'dark'
const variants = ['default', 'dark']

interface IPopupProps {
  variant?: PopupVariant
  renderToggle: (props: {
    open: boolean
    toggleOpen: (e?: React.SyntheticEvent) => void
    ref: React.RefObject<any>
  }) => React.ReactNode
  renderPopup: (props: { toggleOpen: (e?: React.SyntheticEvent) => void }) => React.ReactNode
}

interface IPopupState {
  open: boolean
  pos: {
    top?: number
    transform?: string
    right?: number
    left?: number
  }
  isPositioned: boolean
  placeOnTop: boolean
}

export class Popup extends React.Component<IPopupProps, IPopupState> {
  static propTypes = {
    renderToggle: PropTypes.func.isRequired,
    renderPopup: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(variants),
  }
  constructor(props: IPopupProps) {
    super(props)

    this.contentRef = React.createRef()
    this.toggleRef = React.createRef()
  }

  state = { open: false, pos: {}, isPositioned: false, placeOnTop: false }

  componentDidUpdate(_: IPopupProps, oldState: IPopupState) {
    this.state.open && !oldState.open && this.updateMeasures()
  }

  componentWillUnmount() {
    if (this.state.open) {
      window.document.removeEventListener('resize', this.handleResize)
      removeScrollListener(this.handleScroll)
    }
  }

  handleResize = () => {
    this.state.open && this.updateMeasures()
  }

  handleScroll = throttle(() => {
    this.state.open && this.updateMeasures()
  }, 10)

  handleOpen = () => {
    this.setState(s => ({ ...s, open: true }))
    window.document.addEventListener('resize', this.handleResize)
    addScrollListener(this.handleScroll)
  }

  handleClose = (e?: React.SyntheticEvent) => {
    if (e) {
      stopPropagation(e)
    }
    this.setState(s => ({ ...s, open: false }))
    window.document.removeEventListener('resize', this.handleResize)
    removeScrollListener(this.handleScroll)
  }

  contentRef: React.RefObject<HTMLDivElement>
  toggleRef: React.RefObject<HTMLDivElement>

  static VARIANTS = variants

  updateMeasures() {
    const toggleRect = this.toggleRef.current && this.toggleRef.current.getBoundingClientRect()
    const contentRect = this.contentRef.current && this.contentRef.current.getBoundingClientRect()

    if (!(toggleRect && contentRect)) {
      return
    }

    const windowWidth = window.innerWidth
    const contentWidth = contentRect.width

    const windowHeight = window.innerHeight
    const contentHeight = contentRect.height

    const bottomMarginThreshold = 30

    const fitsOnTop = toggleRect.top + toggleRect.height > contentHeight + bottomMarginThreshold
    const placeOnTop =
      fitsOnTop && windowHeight < contentHeight + toggleRect.top + toggleRect.height + bottomMarginThreshold

    if (contentWidth) {
      const overflowsRight = toggleRect.left + toggleRect.width / 2 + contentWidth / 2 + 20 > windowWidth
      const yPosition = placeOnTop
        ? toggleRect.top - contentHeight - arrowSize
        : toggleRect.top + toggleRect.height + arrowSize
      this.setState(s => ({
        ...s,
        pos: {
          top: 0,
          transform: `translateY(${yPosition}px)`,
          ...(overflowsRight
            ? { right: 20 }
            : {
                left: toggleRect.left + toggleRect.width / 2 - contentWidth / 2,
              }),
        },
        isPositioned: true,
        placeOnTop,
      }))
    }
  }

  render() {
    const { renderToggle, renderPopup, variant = 'default' } = this.props
    const { open, pos, isPositioned, placeOnTop } = this.state
    const toggleOpen = open ? this.handleClose : this.handleOpen

    return (
      <>
        {renderToggle({ open, toggleOpen, ref: this.toggleRef })}
        {open && (
          <Portal>
            <div>
              <Theme>
                {theme => {
                  const styles = getStyles(theme)

                  return (
                    <>
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                      <div
                        onClick={stopPropagation}
                        ref={this.contentRef}
                        {...css(
                          pos,
                          !isPositioned && {
                            ...styles.popup.isPositioned,
                          },
                          { ...styles.popup.wrapper }
                        )}
                      >
                        <FadeIn
                          {...(placeOnTop ? theme.settings.arrowDown : theme.settings.arrowUp)}
                          style={[styles.popup.base, variant && styles.popup.variants[variant]]}
                        >
                          {renderPopup({ toggleOpen })}
                        </FadeIn>
                      </div>
                      <Touchable onClick={this.handleClose} style={styles.popup.outside}>
                        {' '}
                      </Touchable>
                    </>
                  )
                }}
              </Theme>
            </div>
          </Portal>
        )}
      </>
    )
  }
}
